/**
 * Every node `@type` the ontology understands.
 *
 * Four pairs are aliases kept for compatibility with graphs authored before the
 * vocabulary was reconciled (00-ARCHITECTURE §3): `Template`/`Deliverable`,
 * `ArchitectureLens`/`Lens`, `Communication`/`Ceremony`, `Gate`/`GateAgentic`.
 * Both members of a pair carry identical shapes; the second name is the one new
 * code should author. `Gate`/`GateAgentic` is not a hierarchy — the agentic
 * fields are optional on both.
 */
export const NODE_TYPES = {
  Method: 'dpem:Method',
  Phase: 'dpem:Phase',
  Stage: 'dpem:Stage',
  WorkPractice: 'dpem:WorkPractice',
  Activity: 'dpem:Activity',
  Role: 'dpem:Role',
  Gate: 'dpem:Gate',
  GateAgentic: 'dpem:GateAgentic',
  Template: 'dpem:Template',
  Deliverable: 'dpem:Deliverable',
  TemplateVersion: 'dpem:TemplateVersion',
  ArchitectureLens: 'dpem:ArchitectureLens',
  Lens: 'dpem:Lens',
  Communication: 'dpem:Communication',
  Ceremony: 'dpem:Ceremony',
  CommunicationArtifact: 'dpem:CommunicationArtifact',
  Tool: 'dpem:Tool',
  Asset: 'dpem:Asset',
  Agent: 'dpem:Agent',
  AgentSkill: 'dpem:AgentSkill',
  McpServer: 'dpem:McpServer',
  TailoringDecision: 'dpem:TailoringDecision',
} as const;

export type NodeTypeName = keyof typeof NODE_TYPES;
export type NodeTypeIri = (typeof NODE_TYPES)[NodeTypeName];

/**
 * Canonical serialisation order for nodes. Deterministic export sorts by this
 * rank first, so a diff between two exported methods reads top-down: method,
 * structure, actors, control points, work products, catalogue, tailoring.
 */
export const NODE_TYPE_ORDER: readonly NodeTypeIri[] = [
  NODE_TYPES.Method,
  NODE_TYPES.Phase,
  NODE_TYPES.Stage,
  NODE_TYPES.WorkPractice,
  NODE_TYPES.Activity,
  NODE_TYPES.Role,
  NODE_TYPES.Gate,
  NODE_TYPES.GateAgentic,
  NODE_TYPES.Template,
  NODE_TYPES.Deliverable,
  NODE_TYPES.TemplateVersion,
  NODE_TYPES.ArchitectureLens,
  NODE_TYPES.Lens,
  NODE_TYPES.Communication,
  NODE_TYPES.Ceremony,
  NODE_TYPES.CommunicationArtifact,
  NODE_TYPES.Tool,
  NODE_TYPES.Asset,
  NODE_TYPES.Agent,
  NODE_TYPES.AgentSkill,
  NODE_TYPES.McpServer,
  NODE_TYPES.TailoringDecision,
];

/** Groups of `@type` values that mean the same thing, for reference checking. */
export const TYPE_ALIASES = {
  gate: [NODE_TYPES.Gate, NODE_TYPES.GateAgentic],
  deliverable: [NODE_TYPES.Template, NODE_TYPES.Deliverable],
  lens: [NODE_TYPES.ArchitectureLens, NODE_TYPES.Lens],
  ceremony: [NODE_TYPES.Communication, NODE_TYPES.Ceremony],
} as const satisfies Record<string, readonly NodeTypeIri[]>;

export function nodeTypeRank(type: string): number {
  const index = NODE_TYPE_ORDER.indexOf(type as NodeTypeIri);
  return index < 0 ? NODE_TYPE_ORDER.length : index;
}
